import React, { useCallback, useRef } from 'react';

import { useIntl } from 'react-intl-next';

import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { PromptScreen } from '@atlassian/generative-ai-modal/screens/Prompt';

import { useAIExperienceCommonDataContext } from '../../../hooks/useAIExperienceCommonData';
import { useCommandPaletteStateMachine } from '../../../hooks/useCommandPaletteStateMachine';
import { useContentComponentContext } from '../../../hooks/useContentComponentContext';
import { useOutsideClickHandler } from '../../../hooks/useOutsideClickHandler';
import { useAnalyticsFlowEventsQueue } from '../../../utils/analytics/analyticsFlowUtils';
import type { AIExperienceMachineContext } from '../../../utils/xstate/get-ai-experience-service';

import { RovoPromptScreenWithLogic } from './RovoPromptScreenWithLogic';

const usePromptLogic = (
	ctx: AIExperienceMachineContext,
	ai: ReturnType<typeof useAIExperienceCommonDataContext>,
) => {
	const {
		context: { textInput, adfInput },
		send,
	} = useCommandPaletteStateMachine({
		baseGenerate: ctx.baseGenerate,
		startingUserInput: ctx.userInput,
		startingUserADFInput: ctx.userADFInput,
		startingConfig: ctx.configItem,
	});
	const adfRef = useRef<JSONDocNode | undefined>(undefined);
	const textInputRef = useRef<string | undefined>(undefined);

	const onTextChange = useCallback(
		(input: string) => {
			send({ type: 'onTextEntered', input });
			textInputRef.current = input;
		},
		[send],
	);

	const onADFChange = useCallback(
		(adf?: JSONDocNode) => {
			send({ type: 'onADFEntered', adf });
			adfRef.current = adf;
		},
		[send],
	);

	const submit = useCallback(() => {
		const adfInput = adfRef.current;
		const textInput = textInputRef.current;
		if (!textInput?.trim()) {
			return;
		}
		ai.sendToAIExperienceMachine({
			type: 'kickoff with command palette input',
			context: {
				configItem: ctx.configItem,
				userInput: textInput,
				userADFInput: adfInput,
			},
		});
	}, [ctx.configItem, ai]);

	const cancel = useCallback(
		(options?: { shouldClearSelectionContext?: boolean }) => {
			ai.endExperience({ shouldClearSelectionContext: options?.shouldClearSelectionContext });
		},
		[ai],
	);

	const discard = useCallback(() => {
		ai.sendToAIExperienceMachine({
			type: 'update context',
			context: {
				configItem: ctx.configItem,
				userInput: textInput || '',
				userADFInput: adfInput,
			},
		});
		ai.sendToAIExperienceMachine({ type: 'enable discard' });
	}, [adfInput, ai, ctx.configItem, textInput]);

	return { textInput, adfInput, onTextChange, onADFChange, submit, cancel, discard };
};

const usePromptAnalytics = (ctx: AIExperienceMachineContext) => {
	const intl = useIntl();
	useAnalyticsFlowEventsQueue({
		stepName: 'prompt',
		attributes: {
			experienceName: ctx.configItem.key,
			featureId: ctx.configItem.featureId,
			promptTitle: ctx.configItem.promptLabel
				? intl.formatMessage(ctx.configItem.promptLabel)
				: undefined,
			promptType: 'generate',
			usingCommandPalette: true,
			retryPromptCount: ctx.retryPromptCount,
			editCount: ctx.editCount,
		},
	});
};

type Props = { context: AIExperienceMachineContext };

export const PromptScreenWithLogic = ({ context }: Props) => {
	const ai = useAIExperienceCommonDataContext();
	const { isInDocumentStreamingEnabled } = useContentComponentContext();
	const { textInput, adfInput, onTextChange, onADFChange, submit, cancel } = usePromptLogic(
		context,
		ai,
	);
	usePromptAnalytics(context);
	useOutsideClickHandler({
		modalRef: ai.modalRef,
		onCancel: () => {
			// We let the RovoPromptScreenWithLogic handle aborts
			if (!ai.editorPluginAIProvider.onSubmit) {
				cancel();
			}
		},
		onDiscard: () => {},
	});

	if (ai.editorPluginAIProvider.onSubmit) {
		if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
			return null;
		}
		return <RovoPromptScreenWithLogic cancel={cancel} />;
	} else {
		return (
			<PromptScreen
				textInput={textInput}
				adfInput={adfInput}
				onTextChange={onTextChange}
				onADFChange={onADFChange}
				submit={submit}
				cancel={cancel}
				PromptEditor={ai.editorPluginAIProvider.PromptEditor}
				appearance={ai.appearance}
				isAIFC={isInDocumentStreamingEnabled}
			/>
		);
	}
};
